import React, { useState, useRef } from 'react';
import { useCloudinaryUpload } from '../hooks/useCloudinaryUpload';

interface CloudinaryUploaderProps {
  mediaType: 'image' | 'video';
  onUploadComplete?: (url: string) => void;
  className?: string;
  buttonText?: string;
  showPreview?: boolean;
}

export const CloudinaryUploader: React.FC<CloudinaryUploaderProps> = ({
  mediaType,
  onUploadComplete,
  className = '',
  buttonText = 'Upload Media',
  showPreview = true,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, status, mediaUrl, error, progress, reset } = useCloudinaryUpload(mediaType);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    try {
      const url = await upload(file);
      if (onUploadComplete) {
        onUploadComplete(url);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const handleReset = () => {
    setFile(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`rounded-lg border border-gray-200 p-4 ${className}`}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={mediaType === 'image' ? 'image/*' : 'video/*'}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleBrowseClick}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
            disabled={status === 'uploading'}
          >
            Browse {mediaType === 'image' ? 'Image' : 'Video'}
          </button>
          
          {file && (
            <span className="text-sm text-gray-600 truncate max-w-[200px]">
              {file.name}
            </span>
          )}
        </div>

        {file && status === 'idle' && (
          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            {buttonText}
          </button>
        )}

        {status === 'uploading' && (
          <div className="w-full">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-1">Uploading... {Math.round(progress)}%</p>
          </div>
        )}

        {error && (
          <div className="text-red-500 text-sm">
            Error: {error}
            <button 
              onClick={handleReset}
              className="ml-2 text-blue-500 hover:underline"
            >
              Try Again
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-green-600">✓ Upload complete!</span>
              <button 
                onClick={handleReset}
                className="text-sm text-blue-500 hover:underline"
              >
                Upload Another
              </button>
            </div>
            
            {showPreview && (
              <div className="mt-2">
                {mediaType === 'image' ? (
                  <img 
                    src={mediaUrl} 
                    alt="Uploaded" 
                    className="max-w-full h-auto max-h-64 rounded-md" 
                  />
                ) : (
                  <video 
                    src={mediaUrl} 
                    controls 
                    className="max-w-full max-h-64 rounded-md" 
                  />
                )}
              </div>
            )}
            
            <div className="mt-2">
              <p className="text-sm text-gray-600 break-all">
                <span className="font-medium">URL:</span> {mediaUrl}
              </p>
              <button
                onClick={() => navigator.clipboard.writeText(mediaUrl)}
                className="text-sm text-blue-500 hover:underline mt-1"
              >
                Copy URL
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 